import React, { useState } from "react";
import getConfig from "next/config";
import styles from "./ShootingLocationInput.module.scss";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  TextField, 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  StyledEngineProvider,
  } from "@mui/material";
  import {
    Delete,
    Edit
  } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import { PlaceType } from "../../../../models/googleMaps";
import GoogleMapsAutoComplete from "../../../../components/GoogleMapsAutoComplete/GoogleMapsAutoComplete"
import { SingleShootingLocation, ShootingLocations } from "../../../../models/ShootingLocation";
import { setGeocoderInfo } from "../../../../utils/google-location";

export interface RegisterDialogProps {
    value?: ShootingLocations,
    error?: Boolean;
    helperText?: React.ReactNode;
    onChange?: (data: ShootingLocations) => void;
}

const ShootingLocationInput: React.FC<RegisterDialogProps> = (props: RegisterDialogProps) => {
    const [tableData, setTableData] = useState<ShootingLocations>(props.value ? props.value : []);

    const [shootingLocationName, setShootingLocationName] = React.useState("");
    const [shootingLocationNameError, setShootingLocationNameError] = React.useState(false);
    const [shootingLocationLocation, setShootingLocationLocation] = React.useState<PlaceType | null>(null);
    const [shootingLocationLocationKey, setShootingLocationLocationKey] = React.useState(0);
    const [shootingLocationLocationError, setShootingLocationLocationError] = React.useState(false);
    const [shootingLocationMeetingPoint, setShootingLocationMeetingPoint] = React.useState("");
    const [shootingLocationMeetingPointError, setShootingLocationMeetingPointError] = React.useState(false);
    const [shootingLocationPublicTransport, setShootingLocationPublicTransport] = React.useState("");
    const [shootingLocationPublicTransportError, setShootingLocationPublicTransportError] = React.useState(false);
    const [shootingLocationParking, setShootingLocationParking] = React.useState("");
    const [shootingLocationParkingError, setShootingLocationParkingError] = React.useState(false);
    const [isEditShootingLocation, setIsEditShootingLocation] = React.useState(false);
    const [editShootingLocationIndex, setEditShootingLocationIndex] = React.useState<number | null>(null);
    
    const { publicRuntimeConfig } = getConfig();
    
    let shootingLocationValid = false;

    const isShootLocationValid = () => {
        setShootingLocationNameError(shootingLocationName === "");
        setShootingLocationLocationError(shootingLocationLocation === null); 
        setShootingLocationMeetingPointError(shootingLocationMeetingPoint === "") 
        setShootingLocationPublicTransportError(shootingLocationPublicTransport === "")
        setShootingLocationParkingError(shootingLocationParking === "");

        if (shootingLocationName === "" ||
            shootingLocationLocation === null ||
            shootingLocationParking === "") {
                shootingLocationValid = false;
        } else {
            shootingLocationValid = true;
        }

        return shootingLocationValid;
    }

    const addShootingLocation = () => {
        if (!isShootLocationValid()) {
            return;
        }

        let newShootingLocation: SingleShootingLocation = {
            name: shootingLocationName,
            audienceMeetingPointDirections: shootingLocationMeetingPoint,
            publicTransportDirections: shootingLocationPublicTransport,
            parkingDirections: shootingLocationParking,
            placeId: "",
            latitude: 0,
            longitude: 0,
            longLocation: "",
            shortLocation: "",
            detailsJson: "",
            country: "",
            unitOrApartmentNumber: "",
            street: "",
            suburb: "",
            state: "",
            postCode: "",
            canBeDeleted: true,
            canBeEdited: true,
            description: ""
        }
        setGeocoderInfo(shootingLocationLocation, newShootingLocation)

        tableData?.push(newShootingLocation);
        if (props.onChange != null) {
            props.onChange(tableData);
        }

        resetShootingLocationFields();
    }

    const handleEditShootingLocationIcon = (index: number) => {
        setIsEditShootingLocation(!isEditShootingLocation);

        if (isEditShootingLocation) {
            resetShootingLocationFields();
            setEditShootingLocationIndex(null);
        } else {
            setEditShootingLocationIndex(index);
            const shootingLocationDetails = tableData![index];

            if (shootingLocationDetails.name) {
                setShootingLocationName(shootingLocationDetails.name);
            }

            if (shootingLocationDetails.audienceMeetingPointDirections) {
                setShootingLocationMeetingPoint(shootingLocationDetails.audienceMeetingPointDirections);
            }

            if (shootingLocationDetails.publicTransportDirections) {
                setShootingLocationPublicTransport(shootingLocationDetails.publicTransportDirections);
            }

            if (shootingLocationDetails.parkingDirections) {
                setShootingLocationParking(shootingLocationDetails.parkingDirections);
            }
        }
    };

    const editShootingLocation = (index: number | null) => {       
        const isValid = isShootLocationValid();

        if (!isValid) {
            return;
        }

        if (index !== null && index >= 0) {
            let updatedShootingLocation: SingleShootingLocation = {
                name: shootingLocationName,
                audienceMeetingPointDirections: shootingLocationMeetingPoint,
                publicTransportDirections: shootingLocationPublicTransport,
                parkingDirections: shootingLocationParking,
                placeId: "",
                latitude: 0,
                longitude: 0,
                longLocation: "",
                shortLocation: "",
                detailsJson: "",
                country: "",
                unitOrApartmentNumber: "",
                street: "",
                suburb: "",
                state: "",
                postCode: "",
                canBeDeleted: true,
                canBeEdited: true,
                description: "",
            }

            setGeocoderInfo(shootingLocationLocation, updatedShootingLocation);

            tableData![index] = updatedShootingLocation;
            if (props.onChange != null) {
                props.onChange(tableData);
            }

            setIsEditShootingLocation(false);
        }

        resetShootingLocationFields();
    };

    const removeShootingLocation = (index: number) => {
        if (tableData != null) {
            var data = tableData.filter((_, i) => i !== index);
            setTableData(data)
            if (props.onChange != null) {
                props.onChange(data);
            }            
        }            
    }

    const resetShootingLocationFields = () => {
        setShootingLocationName("");
        setShootingLocationLocation(null);
        setShootingLocationLocationKey(shootingLocationLocationKey + 1) // resets the google map autocomplete
        setShootingLocationMeetingPoint("");
        setShootingLocationPublicTransport("");
        setShootingLocationParking("");
    }

    // useEffect(() => {
    //     if (shootingLocationName === "" || 
    //         shootingLocationLocation === null ||
    //         shootingLocationParking === "") {
    //         shootingLocationValid = false;
    //     } else {
    //         shootingLocationValid = true;
    //     }
    // }, [shootingLocationName, shootingLocationLocation, shootingLocationParking]);

    return (
    <StyledEngineProvider injectFirst>
        <FormControl error={props.error ? true : false}>
        <Box sx={{p: 2, border: 1, borderRadius: 1, mb: 2, borderColor: props.error ? "red" : "false" }}>       
            <Table aria-label='table' sx={{mb: 2}}>
                <TableHead>
                    <TableRow>
                        <TableCell>Shooting location(s)</TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody data-cy="modal-shootinglocation-table">
                    {tableData?.map((shootingLocation, index) => (
                        <TableRow key={index} data-id={shootingLocation.id}>
                            <TableCell width={"100%"}>
                                {shootingLocation.name}
                            </TableCell>
                            <TableCell className={styles.iconColumn}>
                                <IconButton
                                    data-cy='edit-shooting-location-icon'
                                    onClick={() => handleEditShootingLocationIcon(index)}
                                    >
                                    <Tooltip
                                        title={isEditShootingLocation ? "Cancel edit shooting location" : "Edit shooting location"}
                                        placement='top'>
                                        <Edit />
                                    </Tooltip>
                                </IconButton>
                            </TableCell>
                            <TableCell className={styles.iconColumn}>
                                {shootingLocation.canBeDeleted &&
                                    <IconButton
                                        data-cy='deleteIcon'
                                        onClick={() => removeShootingLocation(index)}
                                        >
                                        <Tooltip title='Delete shooting location' placement='top'>
                                            <Delete />
                                        </Tooltip>
                                    </IconButton>
                                }
                            </TableCell>
                        </TableRow>
                      ))}
                </TableBody>
            </Table>
            <Grid container>
                <Grid item xs={12} sx={{mb:1}}>
                    <TextField
                        className={styles.input}
                        label="Venue Name"
                        required
                        error={shootingLocationNameError}
                        helperText={shootingLocationNameError ? "Name is mandatory." : ""}
                        value={shootingLocationName}
                        type="text"
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setShootingLocationName(event.target.value);
                            setShootingLocationNameError(event.target.value === "")
                        }}
                        data-cy="modal-shootinglocation-name"
                    />
                </Grid>
                <Grid item xs={12} sx={{mb:1}}>
                    <GoogleMapsAutoComplete 
                        id="google-maps-autocomplete"
                        key={shootingLocationLocationKey}
                        apiKey={publicRuntimeConfig.googleMapsApiKey}
                        value={shootingLocationLocation}
                        error={shootingLocationLocationError}
                        helperText={shootingLocationLocationError ? "Location is mandatory." : ""}
                        onChange={(value) => {
                            setShootingLocationLocation(value);
                            setShootingLocationLocationError(value == null)
                        }}
                        data-cy="modal-shooting-location-location"
                    />
                </Grid>
                <Grid item xs={12} sx={{mb:1}}>
                    <TextField
                        className={styles.input}
                        label="Audience meeting point instructions"
                        required
                        error={shootingLocationMeetingPointError}
                        helperText={shootingLocationMeetingPointError ? "Audience meeting point instructions is mandatory." : ""}
                        value={shootingLocationMeetingPoint}
                        multiline
                        minRows={2}
                        maxRows={4}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setShootingLocationMeetingPoint(event.target.value);
                            setShootingLocationMeetingPointError(event.target.value === "");
                        }}
                        data-cy="modal-shootinglocation-meeting-point"
                    />
                </Grid>
                <Grid item xs={12} sx={{mb:1}}>
                    <TextField
                        className={styles.input}
                        label="Parking instructions"
                        required
                        error={shootingLocationParkingError}
                        helperText={shootingLocationParkingError ? "Parking instructions is mandatory." : ""}
                        value={shootingLocationParking}
                        multiline
                        minRows={2}
                        maxRows={4}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setShootingLocationParking(event.target.value);
                            setShootingLocationParkingError(event.target.value === "");
                        }}
                        data-cy="modal-shootinglocation-parking"
                    />
                </Grid>
                <Grid item xs={12} sx={{mb:1}}>
                    <TextField
                        className={styles.input}
                        label="Public transport instructions"
                        required
                        error={shootingLocationPublicTransportError}
                        helperText={shootingLocationPublicTransportError ? "Public transport instructions is mandatory." : ""}
                        value={shootingLocationPublicTransport}
                        multiline
                        minRows={2}
                        maxRows={4}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setShootingLocationPublicTransport(event.target.value);
                            setShootingLocationPublicTransportError(event.target.value === "");
                        }}
                        data-cy="modal-shootinglocation-public-transport"
                    />
                </Grid>
                <Grid item xs={12} sx={{ display: 'flex' }} alignItems="center" justifyContent="center">
                    {isEditShootingLocation ? (
                        <Button onClick={() => editShootingLocation(editShootingLocationIndex)} 
                                data-cy="modal-shootinglocation-submit">
                            Edit shooting location
                        </Button>
                    ) : (
                        <Button onClick={() => addShootingLocation()} 
                                data-cy="modal-shootinglocation-submit">
                            Add shooting location
                        </Button>
                    )}
                </Grid>                 
            </Grid>
            <FormHelperText>{props.helperText}</FormHelperText>
        </Box>
        </FormControl>
    </StyledEngineProvider> 
    )
};

export default ShootingLocationInput;